'use client'

import React, { useCallback } from 'react'
import type { BlogArchiveBlock, Post } from '@/payload-types'
import { cn } from '@/utilities/ui'
import { useRouter, useSearchParams, usePathname } from 'next/navigation'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import { PostCard } from '@/components/PostCard'
import { Heading } from '@/components/ui/heading'
import { Button, buttonVariants } from '@/components/ui/Button'
import { StickyFilterBar } from '@/components/StickyFilterBar'
import RichText from '@/components/RichText'
import { Text } from '@/components/ui/text'

type BlogArchiveClientProps = {
  heading: BlogArchiveBlock['heading']
  description: BlogArchiveBlock['description']
  posts: Post[]
  categories: Array<{ id: string | number; title: string }>
  selectedCategory: string
  currentPage: number
  totalPages: number
  totalPosts: number
}

const id = 'blog-archive'

export function BlogArchiveClient({
  heading,
  description,
  posts,
  categories,
  selectedCategory,
  currentPage,
  totalPages,
}: BlogArchiveClientProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const resetScrollPosition = useCallback(() => {
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  const handleCategoryChange = useCallback(
    (category: string) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('category', category)
      params.delete('page') // Reset to page 1 when changing category
      router.push(`/blog?${params.toString()}`, { scroll: false })
      resetScrollPosition()
    },
    [router, searchParams, pathname, resetScrollPosition],
  )

  const handlePageChange = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('page', page.toString())
      router.push(`/blog?${params.toString()}`, { scroll: false })
      resetScrollPosition()
    },
    [router, searchParams, pathname, resetScrollPosition],
  )

  const hasNextPage = currentPage < totalPages
  const hasPrevPage = currentPage > 1

  return (
    <div className="bg-rock-1" id={id}>
      <StickyFilterBar
        className="bg-olive-2"
        label={
          <Heading
            size="h3"
            className="py-[15px] hidden md:block text-highlighter relative top-[-3px]"
          >
            Categories
          </Heading>
        }
      >
        <div className="px-4 flex gap-2 items-center py-[10px] md:py-[15px]">
          {categories.map((category) =>
            selectedCategory === category.id.toString() ? (
              <Button
                key={category.id}
                onClick={() => handleCategoryChange(category.id.toString())}
                variant="slime"
                className="text-light font-styreneB text-[1.2rem] h-[40px] capitalize"
              >
                {category.title}
              </Button>
            ) : (
              <Button
                key={category.id}
                onClick={() => handleCategoryChange(category.id.toString())}
                variant="ghost"
                className="text-light font-styreneB text-[1.2rem] h-[40px] capitalize"
              >
                {category.title}
              </Button>
            ),
          )}
        </div>
      </StickyFilterBar>

      <div className="container mx-auto px-4 py-16">
        <div className="mb-12">
          <Heading size="h1" className="text-dark mb-2">
            {heading}
          </Heading>
          {description && <RichText data={description} className="prose-dark" />}
        </div>

        {/* Posts grid */}
        {posts?.length ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[30px] mb-12">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} widthClassName="w-auto" aspectRatio="3/4" />
            ))}
          </div>
        ) : (
          <div className="min-h-[400px] w-full flex items-center justify-center">
            <Text size="xl">No posts found.</Text>
          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  disabled={!hasPrevPage}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
              </PaginationItem>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                // Show current page and surrounding pages
                let pageNumber: number

                if (totalPages <= 5) {
                  // Show all pages if 5 or fewer
                  pageNumber = i + 1
                } else if (currentPage <= 3) {
                  // Near the start
                  pageNumber = i + 1
                } else if (currentPage >= totalPages - 2) {
                  // Near the end
                  pageNumber = totalPages - 4 + i
                } else {
                  // In the middle
                  pageNumber = currentPage - 2 + i
                }

                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      disabled={pageNumber === currentPage}
                      className={cn(
                        buttonVariants({ variant: pageNumber === currentPage ? 'olive' : 'ghost' }),
                      )}
                      onClick={() => handlePageChange(pageNumber)}
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                )
              })}

              <PaginationItem>
                <PaginationNext
                  disabled={!hasNextPage}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      </div>
    </div>
  )
}
